  import Button from "../../../shared/components/UI/Button";
  import Input from "../../../shared/components/UI/Input";
  import Form from "../../../shared/components/UI/Form";

  import { SubmitHandler, useForm } from "react-hook-form";
  import { useNavigate } from "react-router";
  import { useMutation } from "@tanstack/react-query";
  import authService from "../services/authService";
  import { AxiosError } from "axios";
  import { displayNotify } from "../../../shared/services/Notifications/NotificationServices";
  import "../styles/LoginForm.scss";
  import { RegisterPageState } from "../utils/types";

  interface IForm {
  email: string;
  }

  interface Props {
  registerPageState: RegisterPageState;
  setRegisterPageState: React.Dispatch<React.SetStateAction<RegisterPageState>>;
  }

  const passwordChangeNotificationMap: Record<
  number,
  { title: string; description: string }
  > = {
  403: {
    title: "Восстановление пароля запрещено",
    description: "Возможность восстановления пароля отключена администрацией.",
  },
  422: {
    title: "Ошибка валидации",
    description: "Некорректные данные. Проверьте форму.",
  },
  500: {
    title: "Ошибка сервера",
    description: "Что-то пошло не так. Попробуйте позже.",
  },
  };

  function RegisterForm({ registerPageState, setRegisterPageState }: Props) {
  const navigate = useNavigate();
  const { register, handleSubmit, getValues, trigger } = useForm<IForm>({
    mode: "onChange",
  });
  const changePasswordMutation = useMutation({
    mutationFn: (form: IForm) => {
      const { email } = form;
      return authService.changePasswordFetch(email);
    },

    onSuccess: (res) => {
      if (res.status === 200) {
        displayNotify({
          status: "success",
          title: "Успешно!",
          description: "Письмо отправлено",
        });
        navigate('/passwordChangeRequest/confirmed');
      }
      else {
        const message = passwordChangeNotificationMap[res.status ?? 0];
        if (message) {
          displayNotify({
            status: "error",
            title: message.title,
            description: message.description,
          });
        }
        navigate(`/passwordChangeRequest/${res.data.detail}`);
      }
    },
    onError: (error: AxiosError) => {
      try {
        const response = error.response;
        const status = response?.status;
        const data = response?.data as any;
        const detail = data?.detail;


        if (!response) {
          displayNotify({
            status: "error",
            title: "Нет соединения",
            description: "Проверьте интернет или попробуйте позже.",
          });
          return;
        }

        if (detail?.includes("CSRF Failed")) {
          displayNotify({
            status: "error",
            title: "Ошибка безопасности",
            description: "CSRF ошибка. Обновите страницу и попробуйте снова.",
          });
          return;
        }

        const message = passwordChangeNotificationMap[status ?? 0];
        if (message) {
          displayNotify({
            status: "error",
            title: message.title,
            description: message.description,
          });
          return;
        }

        const keys = Object.keys(data)

        if (keys.length === 0) {
          const message = passwordChangeNotificationMap[status ?? 0];

          displayNotify({
            status: "error",
            title: message?.title || "Неизвестная ошибка",
            description: message?.description || "Попробуйте снова позже.",
          });

          return
        }

        keys.forEach(key => {
          displayNotify({
            status: "error",
            title: key,
            description: data[key],
          });
        })


      } catch {
        console.error("Неизвестная ошибка", error);
        displayNotify({
          status: "error",
          title: "Неизвестная ошибка",
          description: "Попробуйте снова позже.",
        });
      }

    },
  });

  const onSubmit: SubmitHandler<IForm> = async () => {
    const values = getValues();

    if (!values.email) {
      displayNotify({
        status: "error",
        title: "Введите email",
        description: "Поле email обязательно",
      });
      return;
    }

    if (!/^[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(values.email)) {
      displayNotify({
        status: "error",
        title: "Некорректный email",
        description: 'Email должен содержать символ "@" и быть корректным',
      });
      return;
    }

    changePasswordMutation.mutate(values);
  };

  const onLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setRegisterPageState("login");
  };

  return (
    <div
      className="login-page"
      style={{ position: "absolute", minHeight: "100vh", overflow: "hidden", backgroundColor: 'rgba(12, 12, 12, 0.5)' }}
    >
      <div />
      <div
        className="login-container"
        style={{ position: "relative", zIndex: 1 }}
      >
        <Form
          noValidate
          className="login_form"
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid #29292A",
            borderRadius: "20px",
          }}
        >
          <div className="login_header">
            <img className="login_logo" src="/skull.svg" alt="logo" />
            <h2 className="login_title">Доброго дня, комрад!</h2>
            <p className="login_subtitle">Введите почту для восстановления пароля</p>
          </div>
          <div className="field-and-button-container">

            <div className="login_input-wrapper">
              <Input
                type="email"
                placeholder="Email"
                className="login_input"
                {...register("email")}
              />
            </div>
            <div className="login_actions">
              <Button
                contraststyle="true"
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="login_submit"
              >
                {changePasswordMutation.isPending ? "Запрос отправлен..." : "Сменить пароль"}
              </Button>
              <p className="forgot-password">
                <a onClick={onLoginClick}>Вернуться ко входу</a>
              </p>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
  }

  export default RegisterForm;